import { Link } from 'react-router-dom'
import {
  AlertCircle,
  ClipboardList,
  Monitor,
  Package,
  Plus,
  Search,
  Settings,
  ShoppingCart,
  Truck,
} from 'lucide-react'
import { useDashboardOverview } from '@/api/dashboard'
import { useWorkOrders } from '@/api/work-order'
import { getApiErrorMessage } from '@/api/client'
import { Loading } from '@/components/Loading'
import { InlineError } from '@/components/ErrorDisplay'
import { useInventoryItemPage } from '@/hooks/inventory'
import { usePurchaseOrderPage } from '@/hooks/procurement'
import { WORK_ORDER_STATUS_LABEL, type WorkOrderStatus } from '@/api/types/work-order'

function formatOrderAmount(items: Array<{ quantity: number; unitPrice?: number | null }>) {
  if (items.length === 0 || items.some((item) => item.unitPrice == null)) return '-'
  const total = items.reduce((sum, item) => sum + item.quantity * (item.unitPrice ?? 0), 0)
  return `CNY ${total.toLocaleString('zh-CN')}`
}

const quickActions = [
  { icon: Plus, label: '采购工作台', href: '/supply-chain/purchase/workbench', tone: 'emerald' },
  { icon: Search, label: '库存总览', href: '/supply-chain/inventory/list', tone: 'blue' },
  { icon: Settings, label: '配件信息', href: '/supply-chain/parts', tone: 'purple' },
  { icon: Monitor, label: '设备档案', href: '/supply-chain/device/list', tone: 'amber' },
] as const

const toneStyles = {
  amber: 'bg-amber-100 text-amber-600',
  blue: 'bg-blue-100 text-blue-600',
  red: 'bg-red-100 text-red-600',
  green: 'bg-green-100 text-green-600',
  purple: 'bg-purple-100 text-purple-600',
  emerald: 'bg-emerald-100 text-emerald-600',
} as const

const hoverStyles = {
  amber: 'hover:bg-amber-50',
  blue: 'hover:bg-blue-50',
  purple: 'hover:bg-purple-50',
  emerald: 'hover:bg-emerald-50',
} as const

export default function Dashboard() {
  const overview = useDashboardOverview()
  const purchases = usePurchaseOrderPage({ page: 0, size: 10 })
  const lowStock = useInventoryItemPage({ lowStockOnly: true, page: 0, size: 5 })
  const inventorySummary = useInventoryItemPage({ page: 0, size: 1 })
  const waitingParts = useWorkOrders({ status: 'WAITING_PARTS', page: 0, size: 5 })

  if (
    overview.isLoading ||
    purchases.isLoading ||
    lowStock.isLoading ||
    inventorySummary.isLoading ||
    waitingParts.isLoading
  ) {
    return (
      <div className="p-6">
        <Loading text="加载工作台数据..." />
      </div>
    )
  }

  if (
    overview.error ||
    purchases.error ||
    lowStock.error ||
    inventorySummary.error ||
    waitingParts.error
  ) {
    return (
      <div className="p-6">
        <InlineError
          message={getApiErrorMessage(
            overview.error ??
              purchases.error ??
              lowStock.error ??
              inventorySummary.error ??
              waitingParts.error
          )}
        />
      </div>
    )
  }

  const purchaseRows =
    purchases.data?.content.filter((item) => item.status === 'CREATED' || item.status === 'APPROVED') ?? []

  const kpis = [
    {
      label: '待处理采购单',
      value: String(purchaseRows.length),
      hint: '待审批 / 待跟进',
      tone: 'amber',
      icon: ShoppingCart,
    },
    {
      label: '在途采购单',
      value: String(
        purchases.data?.content.filter((item) => item.status === 'ORDERED' || item.status === 'IN_TRANSIT')
          .length ?? 0
      ),
      hint: '运输中',
      tone: 'blue',
      icon: Truck,
    },
    {
      label: '低库存预警',
      value: String(overview.data?.lowStockItems ?? 0),
      hint: '真实库存统计',
      tone: 'red',
      icon: AlertCircle,
    },
    {
      label: '配件种类',
      value: String(inventorySummary.data?.totalElements ?? 0),
      hint: '库存 SKU',
      tone: 'green',
      icon: Package,
    },
    {
      label: '待配件工单',
      value: String(overview.data?.waitingPartsWorkOrders ?? 0),
      hint: '等待备件',
      tone: 'purple',
      icon: ClipboardList,
    },
  ] as const

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">工作台</h1>
        <p className="mt-1 text-sm text-gray-500">采购、库存和工单状态的一体化视图</p>
      </div>

      <div className="mb-6 grid gap-4 xl:grid-cols-5">
        {kpis.map((card) => {
          const Icon = card.icon
          return (
            <div key={card.label} className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
              <div className="mb-3 flex items-center justify-between">
                <div className={`flex h-12 w-12 items-center justify-center rounded-xl ${toneStyles[card.tone]}`}>
                  <Icon className="h-6 w-6" />
                </div>
                <span className={`rounded-full px-2 py-1 text-xs font-medium ${toneStyles[card.tone]}`}>
                  {card.hint}
                </span>
              </div>
              <div className="text-3xl font-bold text-gray-900">{card.value}</div>
              <div className="mt-1 text-sm text-gray-500">{card.label}</div>
            </div>
          )
        })}
      </div>

      <div className="grid gap-6 xl:grid-cols-3">
        <div className="xl:col-span-2 rounded-2xl border border-gray-100 bg-white shadow-sm">
          <div className="flex items-center justify-between border-b border-gray-100 p-5">
            <div>
              <h2 className="text-lg font-bold text-gray-900">待处理采购单</h2>
              <p className="mt-1 text-sm text-gray-500">已切到真实采购接口，不再使用本地拼装数据</p>
            </div>
            <Link to="/supply-chain/purchase/list" className="text-sm font-medium text-primary-600 hover:underline">
              查看全部
            </Link>
          </div>
          <div className="overflow-x-auto p-5">
            <table className="w-full">
              <thead>
                <tr className="text-left text-sm text-gray-500">
                  <th className="pb-3 font-medium">采购单号</th>
                  <th className="pb-3 font-medium">关联工单</th>
                  <th className="pb-3 font-medium">供应商</th>
                  <th className="pb-3 font-medium">金额</th>
                  <th className="pb-3 font-medium">状态</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                {purchaseRows.slice(0, 5).map((order) => (
                  <tr key={order.id} className="border-t border-gray-100">
                    <td className="py-4 font-medium text-primary-600">{order.purchaseNo}</td>
                    <td className="py-4 text-gray-600">{order.workOrderNo || '-'}</td>
                    <td className="py-4 text-gray-600">{order.supplierName}</td>
                    <td className="py-4 font-medium text-gray-900">{formatOrderAmount(order.items)}</td>
                    <td className="py-4">
                      <span
                        className={`rounded-full px-2 py-1 text-xs font-medium ${
                          order.status === 'CREATED' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'
                        }`}
                      >
                        {order.status}
                      </span>
                    </td>
                  </tr>
                ))}
                {purchaseRows.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="py-8 text-center text-sm text-gray-400">
                      当前没有待处理采购单
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </div>

        <div className="rounded-2xl border border-gray-100 bg-white shadow-sm">
          <div className="flex items-center justify-between border-b border-gray-100 p-5">
            <div>
              <h2 className="text-lg font-bold text-gray-900">库存预警</h2>
              <p className="mt-1 text-sm text-gray-500">低于安全库存的配件</p>
            </div>
            <Link to="/supply-chain/inventory/alert" className="text-sm font-medium text-primary-600 hover:underline">
              全部预警
            </Link>
          </div>
          <div className="space-y-3 p-5">
            {lowStock.data?.content.length ? (
              lowStock.data.content.map((item) => {
                const critical = item.availableQty === 0
                return (
                  <div
                    key={item.skuCode}
                    className={`rounded-xl border p-3 ${critical ? 'border-red-100 bg-red-50' : 'border-amber-100 bg-amber-50'}`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${critical ? 'bg-red-100' : 'bg-amber-100'}`}>
                          <AlertCircle className={`h-5 w-5 ${critical ? 'text-red-600' : 'text-amber-600'}`} />
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">{item.partName}</div>
                          <div className="text-xs text-gray-500">
                            可用 {item.availableQty} / 安全库存 {item.safetyStockQty}
                          </div>
                        </div>
                      </div>
                      <Link
                        to="/supply-chain/inventory/alert"
                        className={`rounded-lg px-3 py-1.5 text-xs font-medium text-white ${critical ? 'bg-red-500 hover:bg-red-600' : 'bg-amber-500 hover:bg-amber-600'}`}
                      >
                        去处理
                      </Link>
                    </div>
                  </div>
                )
              })
            ) : (
              <div className="py-8 text-center text-sm text-gray-400">当前没有低库存预警</div>
            )}
          </div>
        </div>
      </div>

      <div className="mt-6 grid gap-6 xl:grid-cols-3">
        <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
          <h2 className="mb-4 text-lg font-bold text-gray-900">快捷操作</h2>
          <div className="grid grid-cols-2 gap-3">
            {quickActions.map((action) => {
              const Icon = action.icon
              return (
                <Link
                  key={action.label}
                  to={action.href}
                  className={`rounded-xl p-4 text-center transition ${hoverStyles[action.tone]}`}
                >
                  <div className={`mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-xl ${toneStyles[action.tone]}`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="text-sm font-medium text-gray-700">{action.label}</div>
                </Link>
              )
            })}
          </div>
        </div>

        <div className="xl:col-span-2 rounded-2xl border border-gray-100 bg-white shadow-sm">
          <div className="border-b border-gray-100 p-5">
            <h2 className="text-lg font-bold text-gray-900">待配件工单</h2>
            <p className="mt-1 text-sm text-gray-500">已接入真实工单列表和状态统计</p>
          </div>
          <div className="overflow-x-auto p-5">
            <table className="w-full">
              <thead>
                <tr className="text-left text-sm text-gray-500">
                  <th className="pb-3 font-medium">工单号</th>
                  <th className="pb-3 font-medium">医院 / 设备</th>
                  <th className="pb-3 font-medium">状态</th>
                  <th className="pb-3 font-medium">故障描述</th>
                  <th className="pb-3 font-medium">操作</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                {waitingParts.data?.content.map((item) => (
                  <tr key={item.id} className="border-t border-gray-100">
                    <td className="py-4 font-medium text-gray-900">{item.orderNo}</td>
                    <td className="py-4 text-gray-600">
                      <div>{item.hospitalName || '-'}</div>
                      <div className="text-xs text-gray-400">{item.deviceName || '-'}</div>
                    </td>
                    <td className="py-4 text-gray-600">
                      {WORK_ORDER_STATUS_LABEL[item.status as WorkOrderStatus] ?? item.status}
                    </td>
                    <td className="py-4 text-gray-600">{item.faultDesc || '-'}</td>
                    <td className="py-4">
                      <Link to={`/work-orders/${item.id}`} className="text-primary-600 hover:underline">
                        查看
                      </Link>
                    </td>
                  </tr>
                ))}
                {!waitingParts.data?.content.length ? (
                  <tr>
                    <td colSpan={5} className="py-8 text-center text-sm text-gray-400">
                      当前没有待配件工单
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  )
}
